import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import { User, Loader2, Upload, Plus, X, Search, Check, AlertCircle, Filter, Layers, Calendar, RotateCcw, ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";
import toast from 'react-hot-toast';
import api from "../api";
import MainLayout from "./MainLayout";
import CalendarPopup from "./CalendarPopup";
import "../styles/organizationLeave.css"; // Using list view styles
import "../styles/applyLeavePage.css"; // For form styles if needed
import { useAuth } from "../context/AuthContext";
import { getEmployeeById, getLeaveTypes } from "../services/leaveApiService";
import { Tooltip } from "antd";

// Helper for date formatting
function toYMD(d) {
  if (!d) return "";
  if (typeof d === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
  if (typeof d === "string" && d.includes("T")) return d.slice(0, 10);
  const dt = new Date(d);
  if (isNaN(dt)) return "";
  return dt.toISOString().slice(0, 10);
}
function fmtDate(d) {
  const ymd = toYMD(d);
  if (!ymd) return "--";
  const date = new Date(ymd + "T00:00:00");
  const local = new Date(date.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  return `${String(local.getDate()).padStart(2, '0')}-${String(local.getMonth() + 1).padStart(2, '0')}-${local.getFullYear()}`;
}

const STATUS_META = {
  PENDING: { label: "Pending", cls: "tr-badge--pending" },
  APPROVED: { label: "Approved", cls: "tr-badge--approved" },
  REJECTED: { label: "Rejected", cls: "tr-badge--rejected" },
};

const calculateDuration = (req) => {
  if (req.reason && req.reason.startsWith("[Time:")) {
    const match = req.reason.match(/\[Time:\s*([\d:]+)\s*to\s*([\d:]+)\]/i);
    if (match) {
      const t1 = match[1];
      const t2 = match[2];
      const d1 = new Date(`1970-01-01T${t1}:00`);
      const d2 = new Date(`1970-01-01T${t2}:00`);
      const diffMs = d2 - d1;
      if (diffMs > 0) {
        const hours = Math.floor(diffMs / 3600000);
        const minutes = Math.floor((diffMs % 3600000) / 60000);
        if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`;
        if (hours > 0) return `${hours}h`;
        return `${minutes}m`;
      }
      return `${t1} - ${t2}`;
    }
  }
  
  if (req.end_date && req.start_date) {
    const days = dayjs(req.end_date).diff(dayjs(req.start_date), 'day') + 1;
    return `${days} day${days > 1 ? 's' : ''}`;
  }
  return "1 day";
};

export default function EmployeeRequestForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, logout } = useAuth();
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => setSidebarOpen((p) => !p);

  // user.empId = "SFW-IN-0045"
  const employeeId = user?.employee_id ?? user?.employeeId ?? user?.empId ?? user?.id ?? "employee";
  const userRole = (user?.role || "USER").toUpperCase();
  const isManagerOrAdmin = userRole === "ADMIN" || userRole === "MANAGER" || userRole === "HR" || userRole === "LEAD";

  // Data state
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [filterEmployee, setFilterEmployee] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [dateFilterType, setDateFilterType] = useState("");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  
  const [showFilters, setShowFilters] = useState(false);
  const [groupBy, setGroupBy] = useState("None");
  const [showGroupBy, setShowGroupBy] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Review Modal state
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [reviewMode, setReviewMode] = useState("review");
  const [approveNote, setApproveNote] = useState("");
  const [rejectReason, setRejectReason] = useState("");

  // Form state
  const [requestType, setRequestType] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [isHalfDay, setIsHalfDay] = useState(false);
  const [halfDayType, setHalfDayType] = useState("first");
  const [employeeShift, setEmployeeShift] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [managerName, setManagerName] = useState("Your Manager");
  const [leaveTypes, setLeaveTypes] = useState([]);

  // Fetch list data
  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch all exception requests
      const res = await api.get(`/api/employee-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      let data = res.data?.data || res.data || [];
      if (!Array.isArray(data)) data = [];
      
      // Filter logic based on role
      if (!isManagerOrAdmin) {
        data = data.filter(req => req.employee_id === employeeId || req.employeeId === employeeId);
      }
      
      setRequests(data.sort((a, b) => new Date(b.created_at || b.start_date) - new Date(a.created_at || a.start_date)));
    } catch (err) {
      console.error(err);
      setError(err.message || "Failed to load requests");
    } finally {
      setLoading(false);
    }
  }, [token, employeeId, isManagerOrAdmin]);

  useEffect(() => {
    if (location.state?.selectedDate) {
      setFromDate(location.state.selectedDate);
      setIsModalOpen(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    fetchAll();
    // Fetch holidays for the calendar
    const fetchHolidays = async () => {
      try {
        const holRes = await fetch(`${process.env.REACT_APP_API_BASE_URL || ''}/api/holidays`);
        if (holRes.ok) {
          const holData = await holRes.json();
          setHolidays(holData);
        }
      } catch (e) {
        console.error("Failed to fetch holidays", e);
      }
    };
    fetchHolidays();
  }, [fetchAll]);

  // Fetch Leave Types & Manager Name
  useEffect(() => {
    if (!token) return;
    getLeaveTypes(token)
      .then((types) => setLeaveTypes(types.filter((lt) => lt.is_active && !lt.is_deleted)))
      .catch((err) => console.error("Leave Types fetch error:", err));
      
    if (employeeId) {
      getEmployeeById(employeeId, token)
        .then((emp) => {
          setManagerName(emp?.manager_name ?? "Reporting Head & Lead");
          setEmployeeShift(emp?.shift_name || emp?.shiftTiming || emp?.shift_timing || "");
        })
        .catch((err) => console.error("Employee fetch error:", err));
    }
  }, [token, employeeId]);

  const selectedObj = leaveTypes.find(lt => lt.name === requestType);
  const isHourBased = selectedObj?.unit_type === "HOUR" || (requestType && requestType.toLowerCase().includes("permission")) || requestType === "Forgot Check-in";

  const submitForm = async (e) => {
    e.preventDefault();
    if (!requestType) return toast.error("Please select a request type.");
    if (!fromDate) return toast.error("Please choose a date.");
    if (!isHourBased && !toDate) return toast.error("Please choose an end date.");
    if (isHourBased && (!startTime || !endTime)) return toast.error("Please select start and end time.");
    if (isHalfDay && halfDayType === "custom" && (!startTime || !endTime)) return toast.error("Please select start and end time for custom half day.");
    if (!reason.trim()) return toast.error("Please provide a reason.");

    setSubmitting(true);
    
    const finalStartTime = isHourBased ? startTime : (isHalfDay ? (halfDayType === "first" ? "09:30" : (halfDayType === "second" ? "13:30" : startTime)) : "");
    const finalEndTime = isHourBased ? endTime : (isHalfDay ? (halfDayType === "first" ? "13:30" : (halfDayType === "second" ? "18:30" : endTime)) : "");

    const payload = {
      employee_id: employeeId,
      request_type: requestType,
      start_date: fromDate,
      end_date: isHourBased ? fromDate : toDate,
      reason: reason,
    };

    try {
      await api.post(`/api/employee-requests`, payload, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      toast.success("Exception Request submitted successfully!");
      setIsModalOpen(false);
      
      // Reset form
      setRequestType(""); setFromDate(""); setToDate("");
      setStartTime(""); setEndTime(""); setReason("");
      setIsHalfDay(false); setHalfDayType("first");
      
      fetchAll();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || err.message || "Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  };

  const closeReviewModal = () => {
    setSelectedRequest(null);
    setReviewMode("review");
    setApproveNote("");
    setRejectReason("");
  };

  const handleAction = async (action) => {
    if (!selectedRequest) return;
    if (action === 'REJECTED' && !rejectReason.trim()) {
      return toast.error("Please enter a rejection reason.");
    }
    
    setActionLoading(true);
    try {
      await api.post(`/api/employee-requests/${selectedRequest.id}/approve`, {
        approver_id: employeeId,
        action: action,
        approval_note: action === 'APPROVED' ? (approveNote || 'Approved') : undefined,
        rejection_reason: action === 'REJECTED' ? rejectReason : undefined
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success(`Request ${action.toLowerCase()} successfully`);
      closeReviewModal();
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Action failed");
    } finally {
      setActionLoading(false);
    }
  };

  const filtered = useMemo(() => {
    return requests.filter(r => {
      const q = search.toLowerCase();
      const name = (r.employee_name || r.employeeId || "").toLowerCase();
      const type = (r.request_type || "").toLowerCase();
      const st = (r.status || "PENDING").toLowerCase();
      const startD = fmtDate(r.start_date).toLowerCase();
      const endD = fmtDate(r.end_date).toLowerCase();
      const reasonText = (r.reason || "").toLowerCase();
      const duration = calculateDuration(r).toLowerCase();
      
      const matchSearch = !q || 
        name.includes(q) || 
        type.includes(q) || 
        st.includes(q) || 
        startD.includes(q) || 
        endD.includes(q) || 
        reasonText.includes(q) || 
        duration.includes(q);
        
      const matchEmp = !filterEmployee || name === filterEmployee.toLowerCase();
      const matchType = !filterType || type === filterType.toLowerCase();
      const matchStatus = !filterStatus || (r.status || "PENDING").toUpperCase() === filterStatus;

      let matchDate = true;
      if (dateFilterType) {
        const reqDate = new Date(r.start_date || r.created_at);
        if (dateFilterType === "last7") {
          const d = new Date(); d.setDate(d.getDate() - 7);
          matchDate = reqDate >= d;
        } else if (dateFilterType === "last15") {
          const d = new Date(); d.setDate(d.getDate() - 15);
          matchDate = reqDate >= d;
        } else if (dateFilterType === "lastMonth") {
          const d = new Date();
          d.setMonth(d.getMonth() - 1);
          d.setDate(1);
          const endD = new Date();
          endD.setMonth(endD.getMonth());
          endD.setDate(0);
          matchDate = reqDate >= d && reqDate <= endD;
        } else if (dateFilterType === "custom") {
          const s = customStartDate ? new Date(customStartDate) : null;
          const e = customEndDate ? new Date(customEndDate) : null;
          if (s && e) matchDate = reqDate >= s && reqDate <= e;
          else if (s) matchDate = reqDate >= s;
          else if (e) matchDate = reqDate <= e;
        }
      }

      return matchSearch && matchEmp && matchType && matchStatus && matchDate;
    });
  }, [requests, search, filterEmployee, filterType, filterStatus, dateFilterType, customStartDate, customEndDate]);

  // Extract unique values for filters
  const uniqueEmployees = useMemo(() => Array.from(new Set(requests.map(r => r.employee_name || r.employeeId || ""))).filter(Boolean), [requests]);
  const uniqueTypes = useMemo(() => Array.from(new Set(requests.map(r => r.request_type || ""))).filter(Boolean), [requests]);

  const sortedAndGrouped = useMemo(() => {
    if (groupBy === "None") return filtered;
    return [...filtered].sort((a, b) => {
      let keyA = "", keyB = "";
      if (groupBy === "Employee") {
        keyA = (a.employee_name || a.employeeId || "").toLowerCase();
        keyB = (b.employee_name || b.employeeId || "").toLowerCase();
      } else if (groupBy === "Request Type") {
        keyA = (a.request_type || "").toLowerCase();
        keyB = (b.request_type || "").toLowerCase();
      } else if (groupBy === "Status") {
        keyA = (a.status || "PENDING").toUpperCase();
        keyB = (b.status || "PENDING").toUpperCase();
      }
      return keyA.localeCompare(keyB);
    });
  }, [filtered, groupBy]);

  const paginated = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return sortedAndGrouped.slice(start, start + itemsPerPage);
  }, [sortedAndGrouped, page, itemsPerPage]);

  return (
    <>
      <MainLayout sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar} title="Employee Requests">
        <div style={{ flex: 1, position: 'relative' }}>

          <div className="tr-wrap" style={{ padding: 0 }}>
            {/* Filter and Action Section */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '8px 14px', flex: '1', minWidth: '200px', maxWidth: '420px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                <Search size={16} color="#94a3b8" style={{ flexShrink: 0 }} />
                <input style={{ border: 'none', outline: 'none', padding: '4px 10px', width: '100%', fontSize: '14px', color: '#1e293b', background: 'transparent' }} placeholder="Search by name, type or employee ID..." value={search} onChange={e => setSearch(e.target.value)} />
                {search && <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 0, display: 'flex' }}><X size={14} /></button>}
              </div>
              
              {/* Filter Button */}
              <div style={{ position: 'relative' }}>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button onClick={() => { setShowFilters(!showFilters); setShowGroupBy(false); }} style={{ display: 'flex', alignItems: 'center', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '6px 14px', gap: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', color: '#334155' }}>
                    <Filter size={14} color="#64748b" /> Filter
                  </button>
                </div>
                {showFilters && (
                  <div style={{ position: 'absolute', top: '100%', left: '0', marginTop: '8px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 10, display: 'flex', flexDirection: 'column', gap: '16px', minWidth: '280px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <h4 style={{ margin: 0, fontSize: '12px', fontWeight: 'bold', color: '#64748b', letterSpacing: '0.5px' }}>FILTERS</h4>
                      <button onClick={() => setShowFilters(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}><X size={16} /></button>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <select className="al-input" value={filterEmployee} onChange={e => setFilterEmployee(e.target.value)} style={{ padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '13px' }}>
                        <option value="">All Employees</option>
                        {uniqueEmployees.map(emp => <option key={emp} value={emp}>{emp}</option>)}
                      </select>

                      <select className="al-input" value={filterType} onChange={e => setFilterType(e.target.value)} style={{ padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '13px' }}>
                        <option value="">All Request Types</option>
                        {uniqueTypes.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>

                      <select className="al-input" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '13px' }}>
                        <option value="">All Statuses</option>
                        <option value="PENDING">Pending</option>
                        <option value="APPROVED">Approved</option>
                        <option value="REJECTED">Rejected</option>
                      </select>
                    </div>
                    
                    <div style={{ height: '1px', background: '#e2e8f0', margin: '4px 0' }}></div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <h4 style={{ margin: 0, fontSize: '12px', fontWeight: 'bold', color: '#64748b' }}>DATE RANGE</h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button onClick={() => setDateFilterType(dateFilterType === 'last7' ? '' : 'last7')} style={{ flex: 1, padding: '8px', borderRadius: '8px', border: `1px solid ${dateFilterType === 'last7' ? '#3b82f6' : '#e2e8f0'}`, background: dateFilterType === 'last7' ? '#eff6ff' : '#fff', color: dateFilterType === 'last7' ? '#2563eb' : '#64748b', fontSize: '13px', cursor: 'pointer' }}>Last 7 Days</button>
                          <button onClick={() => setDateFilterType(dateFilterType === 'last15' ? '' : 'last15')} style={{ flex: 1, padding: '8px', borderRadius: '8px', border: `1px solid ${dateFilterType === 'last15' ? '#3b82f6' : '#e2e8f0'}`, background: dateFilterType === 'last15' ? '#eff6ff' : '#fff', color: dateFilterType === 'last15' ? '#2563eb' : '#64748b', fontSize: '13px', cursor: 'pointer' }}>Last 15 Days</button>
                        </div>
                        <button onClick={() => setDateFilterType(dateFilterType === 'lastMonth' ? '' : 'lastMonth')} style={{ width: '100%', padding: '8px', borderRadius: '8px', border: `1px solid ${dateFilterType === 'lastMonth' ? '#3b82f6' : '#e2e8f0'}`, background: dateFilterType === 'lastMonth' ? '#eff6ff' : '#fff', color: dateFilterType === 'lastMonth' ? '#2563eb' : '#64748b', fontSize: '13px', cursor: 'pointer' }}>Last Month</button>
                      </div>

                      <div style={{ marginTop: '4px' }}>
                        <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Custom Date:</label>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <input type="date" value={customStartDate} onChange={(e) => { setDateFilterType('custom'); setCustomStartDate(e.target.value); }} style={{ padding: '8px', borderRadius: '8px', border: '1px solid #e2e8f0', outline: 'none', fontSize: '13px', width: '100%' }} />
                          <input type="date" value={customEndDate} onChange={(e) => { setDateFilterType('custom'); setCustomEndDate(e.target.value); }} style={{ padding: '8px', borderRadius: '8px', border: '1px solid #e2e8f0', outline: 'none', fontSize: '13px', width: '100%' }} />
                        </div>
                      </div>
                    </div>
                    
                    <div style={{ height: '1px', background: '#e2e8f0', margin: '4px 0' }}></div>
                    
                    <button onClick={() => { 
                      setSearch(""); setFilterEmployee(""); setFilterType(""); setFilterStatus(""); 
                      setDateFilterType(""); setCustomStartDate(""); setCustomEndDate(""); setShowFilters(false); 
                    }} style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#fff', border: '1px solid #fee2e2', borderRadius: '8px', padding: '8px', gap: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', color: '#dc2626' }}>
                      <RotateCcw size={13} /> Reset
                    </button>
                  </div>
                )}
              </div>
              
              {/* Group By Button */}
              <div style={{ position: 'relative' }}>
                <button onClick={() => { setShowGroupBy(!showGroupBy); setShowFilters(false); }} style={{ display: 'flex', alignItems: 'center', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '6px 14px', gap: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', color: '#334155' }}>
                  <Layers size={14} color="#64748b" /> Group by
                </button>
                {showGroupBy && (
                  <div style={{ position: 'absolute', top: '100%', left: '0', marginTop: '8px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '8px 0', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 10, minWidth: '160px' }}>
                    {["Employee", "Request Type", "Status", "None"].map(opt => (
                      <div 
                        key={opt} 
                        onClick={() => { setGroupBy(opt); setShowGroupBy(false); }}
                        style={{ padding: '8px 16px', fontSize: '13px', cursor: 'pointer', background: groupBy === opt ? '#f1f5f9' : 'transparent', color: groupBy === opt ? '#0f172a' : '#475569' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                        onMouseLeave={e => e.currentTarget.style.background = groupBy === opt ? '#f1f5f9' : 'transparent'}
                      >
                        {opt}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Action Buttons on the right */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginLeft: 'auto' }}>
                <Tooltip title="Calendar View">
                  <button className="header-calendar-btn" onClick={() => setShowCalendar(true)} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '50%', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#3b82f6' }}>
                    <CalendarDays size={16} />
                  </button>
                </Tooltip>
                <button className="tr-btn tr-btn--approve" onClick={() => setIsModalOpen(true)} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#3b82f6', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontWeight: '600', cursor: 'pointer' }}>
                  <Plus size={14} /> New Request
                </button>
              </div>
            </div>

            <div className="tr-table-card" style={{ marginTop: '20px' }}>
              <div className="tr-table-scroll">
                {loading ? (
                  <div className="tr-empty"><Loader2 size={24} className="tr-spinner" /><p>Loading requests...</p></div>
                ) : error ? (
                  <div className="tr-empty" style={{ color: "#DC2626" }}><AlertCircle size={24} /><p>{error}</p></div>
                ) : filtered.length === 0 ? (
                  <div className="tr-empty"><p>No requests found</p></div>
                ) : (
                  <>
                    <table className="tr-table">
                      <thead>
                        <tr>
                          <th className="tr-th">Employee</th>
                          <th className="tr-th">Request Type</th>
                          <th className="tr-th">Start Date</th>
                          <th className="tr-th">End Date</th>
                          <th className="tr-th" style={{ textAlign: 'center' }}>Duration</th>
                          <th className="tr-th">Reason</th>
                          <th className="tr-th">Status</th>
                          {isManagerOrAdmin && <th className="tr-th" style={{ width: '130px', textAlign: 'center' }}>Actions</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {paginated.map((req, index) => {
                          let currentGroup = null;
                          if (groupBy !== "None") {
                            if (groupBy === "Employee") currentGroup = req.employee_name || req.employeeId || "Unknown Employee";
                            else if (groupBy === "Request Type") currentGroup = req.request_type || "Unknown Type";
                            else if (groupBy === "Status") currentGroup = (req.status || "PENDING").toUpperCase();
                          }
                          
                          let prevGroup = null;
                          if (index > 0 && groupBy !== "None") {
                            const prevReq = paginated[index - 1];
                            if (groupBy === "Employee") prevGroup = prevReq.employee_name || prevReq.employeeId || "Unknown Employee";
                            else if (groupBy === "Request Type") prevGroup = prevReq.request_type || "Unknown Type";
                            else if (groupBy === "Status") prevGroup = (prevReq.status || "PENDING").toUpperCase();
                          }
                          const showGroupHeader = groupBy !== "None" && currentGroup !== prevGroup;
                          
                          const status = (req.status || "PENDING").toUpperCase();
                          const meta = STATUS_META[status] || { label: status, cls: "" };
                          return (
                            <React.Fragment key={req.id}>
                              {showGroupHeader && (
                                <tr>
                                  <td colSpan={isManagerOrAdmin ? "7" : "6"} style={{ background: '#f8fafc', fontWeight: 'bold', borderBottom: '1px solid #e2e8f0', padding: '12px 16px', color: '#334155' }}>
                                    {currentGroup}
                                  </td>
                                </tr>
                              )}
                              <tr className="tr-row">
                              <td className="tr-td tr-td--muted"><strong>{req.employee_name || req.employeeId || employeeId}</strong></td>
                              <td className="tr-td tr-td--muted">{req.request_type}</td>
                              <td className="tr-td tr-td--muted">{fmtDate(req.start_date)}</td>
                              <td className="tr-td tr-td--muted">{fmtDate(req.end_date)}</td>
                              <td className="tr-td tr-td--muted" style={{ textAlign: 'center', fontWeight: 'bold' }}>
                                {calculateDuration(req)}
                              </td>
                              <td className="tr-td tr-td--muted" style={{ maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={req.reason}>{req.reason || "--"}</td>
                              <td className="tr-td"><span className={`tr-status-badge ${meta.cls}`}>{meta.label}</span></td>
                              {isManagerOrAdmin && (
                                <td className="tr-td" style={{ textAlign: 'center' }}>
                                  {status === "PENDING" ? (
                                    <button className="tr-review-btn" onClick={() => setSelectedRequest(req)}>
                                      <Check size={13} /> Review
                                    </button>
                                  ) : <span className="tr-td--muted">--</span>}
                                </td>
                              )}
                            </tr>
                            </React.Fragment>
                          )
                        })}
                      </tbody>
                    </table>
                    
                    {sortedAndGrouped.length > 0 && (
                      <div className="em-pagination" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', background: '#fff', borderTop: '1px solid #e2e8f0', borderRadius: '0 0 12px 12px' }}>
                        <div className="em-pagination__left" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                          <span className="em-pagination__info" style={{ fontSize: '13px', color: '#64748b' }}>
                            Showing {Math.min((page - 1) * itemsPerPage + 1, sortedAndGrouped.length)}–{Math.min(page * itemsPerPage, sortedAndGrouped.length)} of {sortedAndGrouped.length}
                          </span>
                          <div className="em-rows-selector" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span className="em-rows-label" style={{ fontSize: '13px', color: '#64748b' }}>Rows:</span>
                            <select
                              className="em-rows-select"
                              value={itemsPerPage}
                              onChange={(e) => { setItemsPerPage(Number(e.target.value)); setPage(1); }}
                              style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid #e2e8f0', outline: 'none', fontSize: '13px', cursor: 'pointer' }}
                            >
                              <option value={10}>10</option>
                              <option value={20}>20</option>
                              <option value={30}>30</option>
                              <option value={40}>40</option>
                            </select>
                          </div>
                        </div>
                        <div className="em-pagination__btns" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <button 
                            className="em-page-btn" 
                            disabled={page === 1} 
                            onClick={() => setPage(page - 1)}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', borderRadius: '6px', border: '1px solid #e2e8f0', background: '#fff', cursor: page === 1 ? 'not-allowed' : 'pointer', color: page === 1 ? '#cbd5e1' : '#64748b' }}
                          >
                            <ChevronLeft size={15} />
                          </button>
                          
                          {Array.from({ length: Math.ceil(sortedAndGrouped.length / itemsPerPage) || 1 }, (_, i) => (
                            <button 
                              key={i + 1} 
                              onClick={() => setPage(i + 1)}
                              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', borderRadius: '6px', border: page === i + 1 ? '1px solid #3b82f6' : '1px solid #e2e8f0', background: page === i + 1 ? '#3b82f6' : '#fff', cursor: 'pointer', color: page === i + 1 ? '#fff' : '#64748b', fontSize: '13px', fontWeight: '500' }}
                            >
                              {i + 1}
                            </button>
                          ))}
                          
                          <button 
                            className="em-page-btn" 
                            disabled={page >= Math.ceil(sortedAndGrouped.length / itemsPerPage)} 
                            onClick={() => setPage(page + 1)}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', borderRadius: '6px', border: '1px solid #e2e8f0', background: '#fff', cursor: page >= Math.ceil(sortedAndGrouped.length / itemsPerPage) ? 'not-allowed' : 'pointer', color: page >= Math.ceil(sortedAndGrouped.length / itemsPerPage) ? '#cbd5e1' : '#64748b' }}
                          >
                            <ChevronRight size={15} />
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </MainLayout>

      {/* Review / Reject Modal */}
      {selectedRequest && (
        <div className="tr-modal-backdrop" onClick={() => !actionLoading && closeReviewModal()}>
          <div className="tr-modal" onClick={e => e.stopPropagation()} style={{ width: '450px' }}>
            <div className="tr-modal-header">
              <div>
                <h2 className="tr-modal-title">
                  {reviewMode === "reject" ? "Reject Request" : "Review Request"}
                </h2>
                <p className="tr-modal-sub">
                  {reviewMode === "reject"
                    ? "Please provide a reason for rejection."
                    : "Review the request details and approve or reject."}
                </p>
              </div>
              <button className="tr-modal-close" onClick={() => !actionLoading && closeReviewModal()}>
                <X size={18} />
              </button>
            </div>

            {reviewMode === "review" && (
              <div className="tr-modal-body">
                <div style={{ padding: '20px', borderBottom: '1px solid #f1f5f9' }}>
                  <p style={{ margin: '0 0 12px', fontSize: '15px', fontWeight: 600 }}>{selectedRequest.employee_name || selectedRequest.employeeId}</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}>
                    <span style={{ color: '#64748b' }}>Request Type:</span>
                    <span style={{ fontWeight: 500 }}>{selectedRequest.request_type}</span>
                    <span style={{ color: '#64748b' }}>Date Range:</span>
                    <span style={{ fontWeight: 500 }}>{fmtDate(selectedRequest.start_date)} {selectedRequest.end_date && selectedRequest.end_date !== selectedRequest.start_date ? ` – ${fmtDate(selectedRequest.end_date)}` : ''}</span>
                    <span style={{ color: '#64748b' }}>Reason:</span>
                    <span style={{ fontWeight: 500 }}>{selectedRequest.reason}</span>
                  </div>
                </div>

                <div className="tr-reject-wrap" style={{ padding: "16px 20px" }}>
                  <label style={{ fontSize: "12px", fontWeight: 600, color: "#6b7280", display: "block", marginBottom: "6px" }}>
                    Approval Note <span style={{ fontWeight: 400, color: "#9ca3af" }}>(Optional)</span>
                  </label>
                  <textarea
                    className="tr-reject-input"
                    placeholder="Add a note for the employee (optional)..."
                    value={approveNote}
                    rows={2}
                    onChange={e => setApproveNote(e.target.value)}
                  />
                </div>
              </div>
            )}

            {reviewMode === "reject" && (
              <div className="tr-reject-wrap" style={{ padding: "20px" }}>
                <textarea
                  className="tr-reject-input"
                  placeholder="Enter rejection reason..."
                  value={rejectReason}
                  rows={4}
                  onChange={e => setRejectReason(e.target.value)}
                />
              </div>
            )}

            <div className="tr-modal-footer">
              {reviewMode === "review" ? (
                <>
                  <button className="tr-btn tr-btn--outline" onClick={() => setReviewMode("reject")} disabled={actionLoading}>
                    <X size={14} /> Reject
                  </button>
                  <button className="tr-btn tr-btn--approve" onClick={() => handleAction('APPROVED')} disabled={actionLoading}>
                    {actionLoading ? <Loader2 size={14} className="tr-spinner" /> : <Check size={14} />}
                    {actionLoading ? "Approving…" : "Approve"}
                  </button>
                </>
              ) : (
                <>
                  <button className="tr-btn tr-btn--outline" onClick={() => setReviewMode("review")} disabled={actionLoading}>
                    Back
                  </button>
                  <button className="tr-btn tr-btn--danger" onClick={() => handleAction('REJECTED')} disabled={actionLoading}>
                    {actionLoading ? <Loader2 size={14} className="tr-spinner" /> : null}
                    {actionLoading ? "Rejecting…" : "Confirm Rejection"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Side-Panel Modal for New Request */}
      {isModalOpen && (
        <>
          <div className="tr-modal-backdrop" onClick={() => setIsModalOpen(false)} style={{ zIndex: 1000 }} />
          <div className="tr-modal" style={{ 
              position: 'fixed', top: 0, right: 0, bottom: 0, left: 'auto', transform: 'none',
              width: '650px', maxWidth: '100vw', height: '100vh', maxHeight: '100vh',
              margin: 0, borderRadius: 0, 
              animation: 'slideInRight 0.3s ease-out',
              zIndex: 1001,
              display: 'flex', flexDirection: 'column',
              background: '#fff',
              boxShadow: '-4px 0 24px rgba(0,0,0,0.1)'
            }} onClick={e => e.stopPropagation()}>
            <style>{`
              @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
            `}</style>
            
            <div className="tr-modal-header" style={{ padding: '28px 32px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h2 className="tr-modal-title" style={{ margin: 0, fontSize: '22px', fontWeight: 'bold' }}>Create Exception Request</h2>
                <p className="tr-modal-sub" style={{ margin: '6px 0 0 0', color: '#64748b', fontSize: '14px' }}>Fill in the details to log a new request</p>
              </div>
              <button className="tr-modal-close" onClick={() => setIsModalOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <X size={24} color="#64748b" />
              </button>
            </div>
            
            <div className="tr-modal-body" style={{ padding: '32px', overflowY: 'auto', flex: 1, background: '#fff' }}>
              <form onSubmit={submitForm} style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  {/* Request Type */}
                  <div className="al-field">
                    <label className="al-label" style={{ display: 'block', marginBottom: '8px', fontWeight: 600, fontSize: '13px', color: '#334155' }}>REQUEST TYPE <span style={{color: '#ef4444'}}>*</span></label>
                    <select className="al-input" value={requestType} onChange={(e) => {
                      const val = e.target.value;
                      setRequestType(val);
                      if (val === "Forgot Check-in") {
                        setStartTime("09:30");
                        setEndTime("18:30");
                      } else if (requestType === "Forgot Check-in") {
                        setStartTime("");
                        setEndTime("");
                      }
                    }} style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '13px' }}>
                      <option value="">Select request type</option>
                      <option value="Forgot Check-in">Forgot Check-in</option>
                      {leaveTypes.map((lt) => <option key={lt.id} value={lt.name}>{lt.name}</option>)}
                    </select>
                  </div>
                  
                  {/* Date */}
                  <div className="al-field">
                    <label className="al-label" style={{ display: 'block', marginBottom: '8px', fontWeight: 600, fontSize: '12px', color: '#334155' }}>{isHourBased ? "DATE " : "START DATE "}<span style={{color: '#ef4444'}}>*</span></label>
                    <input type="date" className="al-input" value={fromDate} onClick={(e) => e.target.showPicker()} onChange={(e) => setFromDate(e.target.value)} style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '13px' }} />
                  </div>

                  {!isHourBased && (
                    <div className="al-field">
                      <label className="al-label" style={{ display: 'block', marginBottom: '8px', fontWeight: 600, fontSize: '12px', color: '#334155' }}>END DATE <span style={{color: '#ef4444'}}>*</span></label>
                      <input type="date" className="al-input" value={toDate} min={fromDate} onClick={(e) => e.target.showPicker()} onChange={(e) => setToDate(e.target.value)} style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '13px' }} />
                    </div>
                  )}
                  
                  {/* ── Half Day toggle ── */}
                  {!isHourBased && fromDate && toDate && fromDate === toDate && (
                    <div className="al-halfday-wrap" style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '24px', flexWrap: 'wrap', marginBottom: '12px', marginTop: '-12px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', fontWeight: 600, background: '#e0f2fe', color: '#0369a1', padding: '6px 14px', borderRadius: '6px', border: '1.5px solid #7dd3fc', transition: 'all 0.15s', userSelect: 'none' }}>
                        <input 
                          type="checkbox" 
                          name="halfDayType_req" 
                          checked={isHalfDay && halfDayType === "first"} 
                          onChange={(e) => {
                            if (e.target.checked) {
                              setIsHalfDay(true);
                              setHalfDayType("first");
                              setStartTime("09:30");
                              setEndTime("13:30");
                            } else {
                              setIsHalfDay(false);
                              setHalfDayType("");
                              setStartTime("");
                              setEndTime("");
                            }
                          }} 
                          style={{ margin: 0, cursor: 'pointer', accentColor: '#0369a1' }}
                        />
                        First Half (09:30 AM - 01:30 PM)
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', fontWeight: 600, background: '#fef3c7', color: '#b45309', padding: '6px 14px', borderRadius: '6px', border: '1.5px solid #fcd34d', transition: 'all 0.15s', userSelect: 'none' }}>
                        <input 
                          type="checkbox" 
                          name="halfDayType_req" 
                          checked={isHalfDay && halfDayType === "second"} 
                          onChange={(e) => {
                            if (e.target.checked) {
                              setIsHalfDay(true);
                              setHalfDayType("second");
                              setStartTime("14:30");
                              setEndTime("18:30");
                            } else {
                              setIsHalfDay(false);
                              setHalfDayType("");
                              setStartTime("");
                              setEndTime("");
                            }
                          }} 
                          style={{ margin: 0, cursor: 'pointer', accentColor: '#b45309' }}
                        />
                        Second Half (02:30 PM - 06:30 PM)
                      </label>
                    </div>
                  )}

                  {isHourBased && (
                    <>
                      <div className="al-field">
                        <label className="al-label" style={{ display: 'block', marginBottom: '8px', fontWeight: 600, fontSize: '12px', color: '#334155' }}>START TIME <span style={{color: '#ef4444'}}>*</span></label>
                        <input type="time" className="al-input" value={startTime} onChange={(e) => setStartTime(e.target.value)} onClick={(e) => e.target.showPicker()} style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '13px' }} />
                      </div>
                      <div className="al-field">
                        <label className="al-label" style={{ display: 'block', marginBottom: '8px', fontWeight: 600, fontSize: '12px', color: '#334155' }}>END TIME <span style={{color: '#ef4444'}}>*</span></label>
                        <input type="time" className="al-input" value={endTime} onChange={(e) => setEndTime(e.target.value)} onClick={(e) => e.target.showPicker()} style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '13px' }} />
                      </div>
                    </>
                  )}
                </div>

                {/* Reason */}
                <div className="al-field" style={{ marginBottom: "16px" }}>
                  <label className="al-label" style={{ display: 'block', marginBottom: '8px', fontWeight: 600, fontSize: '12px', color: '#334155' }}>REASON <span style={{color: '#ef4444'}}>*</span></label>
                  <textarea className="al-textarea" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Describe the reason..." rows={3} style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', resize: 'vertical', minHeight: '80px', fontSize: '13px', fontFamily: 'inherit' }} />
                </div>

                {/* Manager */}
                <div className="al-manager-box" style={{ marginBottom: '24px', background: '#f8f9fa', padding: '16px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '14px', border: '1px solid #e2e8f0' }}>
                  <div className="al-manager-avatar" style={{ background: '#e2e8f0', padding: '10px', borderRadius: '50%' }}><User size={18} color="#475569" /></div>
                  <div>
                    <p className="al-manager-label" style={{ margin: 0, fontSize: '12px', color: '#64748b', fontWeight: 500 }}>Request will be sent to</p>
                    <p className="al-manager-name" style={{ margin: '4px 0 0', fontSize: '15px', fontWeight: 600, color: '#1e293b' }}>{managerName}</p>
                  </div>
                </div>
                
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '14px', marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #e2e8f0' }}>
                  <button type="button" className="tr-btn tr-btn--outline" onClick={() => setIsModalOpen(false)} style={{ padding: '12px 24px', borderRadius: '8px', fontWeight: 600 }}>Cancel</button>
                  <button type="submit" className="tr-btn tr-btn--approve" disabled={submitting} style={{ padding: '12px 24px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600, background: '#3b82f6', color: '#fff', border: 'none' }}>
                    {submitting ? <Loader2 size={18} className="tr-spinner" /> : null}
                    {submitting ? "Submitting..." : "Submit Request"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}
      
      <CalendarPopup
        visible={showCalendar}
        onClose={() => setShowCalendar(false)}
        mode="request"
        holidays={holidays}
        requests={requests}
      />
    </>
  );
}
